import React, { useCallback, useEffect, useRef, useState } from 'react';
import OsScroll from './ui/OsScroll';
import { invokeAsExtension } from '../extensions/capabilities/enforce';
import { DB_QUERY_COMMANDS, listColumnsMeta, pickDisplayColumns, quoteIdentFor } from '../extensions/dbEngines';
import { buildDictionaryQuery } from './dbGridSql';
import { cellToText } from './dbCellText';

/**
 * Pick a foreign-key value by what it means rather than by its number.
 *
 * A column holding `4711` tells you nothing; the row it points at has a name.
 * This lists the referenced table as "key — label" and stages the key when you
 * choose one, which is the DBeaver "value from dictionary" idea.
 *
 * Always searchable and always LIMIT-ed, rather than HeidiSQL's approach of
 * only offering a dropdown below a row-count threshold: a bounded, searchable
 * list behaves the same on ten rows as on ten million.
 */

const DICTIONARY_LIMIT = 200;
const SEARCH_DEBOUNCE_MS = 220;

export default function DbFkDictionary({ engine, extensionId, connId, dbName, fk, value, onPick }) {
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState(null);
  const [columns, setColumns] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  // Every load carries a token; only the newest one is allowed to write state,
  // so a slow first query cannot land on top of a newer search's results.
  const loadIdRef = useRef(0);

  // Composite keys have no single column to stage, and picking one half of a
  // pair would silently write an inconsistent row.
  const keyColumn = fk?.refColumns?.length === 1 ? fk.refColumns[0] : null;

  const load = useCallback(
    async (term) => {
      if (!keyColumn) return;
      const id = ++loadIdRef.current;
      setLoading(true);
      setError(null);
      try {
        // The schema the key POINTS AT, which is not always the one being
        // browsed — `getForeignKeys` fills refDb from REFERENCED_TABLE_SCHEMA
        // on MySQL and fns.nspname on Postgres (null on SQLite, which has no
        // schemas). Using the tab's dbName sent every cross-schema lookup to
        // `<current>.<refTable>`, which does not exist, so the dictionary
        // errored on exactly the keys the navigate button opened correctly —
        // it has always used `hit.fk.refDb || tab.dbName`.
        const refDb = fk.refDb || dbName;
        const meta = await listColumnsMeta(engine, extensionId, connId, refDb, fk.refTable);
        if (loadIdRef.current !== id) return;
        const displayColumns = pickDisplayColumns(meta, keyColumn);
        const tableRef =
          engine === 'sqlite'
            ? quoteIdentFor(engine, fk.refTable)
            : `${quoteIdentFor(engine, refDb)}.${quoteIdentFor(engine, fk.refTable)}`;
        const { sql, params } = buildDictionaryQuery({
          engine,
          tableRef,
          keyColumn,
          displayColumns,
          search: term,
          limit: DICTIONARY_LIMIT,
        });
        const res = await invokeAsExtension(DB_QUERY_COMMANDS[engine], { extensionId, connId, sql, params });
        if (loadIdRef.current !== id) return;
        setColumns(res.columns);
        setRows(res.rows);
      } catch (e) {
        if (loadIdRef.current !== id) return;
        setError(String(e?.message || e));
        setRows([]);
      } finally {
        if (loadIdRef.current === id) setLoading(false);
      }
    },
    [engine, extensionId, connId, dbName, fk, keyColumn]
  );

  // Debounced: typing in the box would otherwise send a query per keystroke to
  // a table that may be large.
  useEffect(() => {
    const t = setTimeout(() => load(search), search ? SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(t);
  }, [search, load]);

  if (!fk) return null;

  if (!keyColumn) {
    return (
      <div className="dbm-fkdict">
        <div className="dbm-fkdict-head">References {fk.refTable}</div>
        <div className="dbm-fkdict-note">
          This key spans {fk.refColumns.length} columns, so a value cannot be chosen from here —
          staging one half would leave the row inconsistent.
        </div>
      </div>
    );
  }

  const current = cellToText(value);

  return (
    <div className="dbm-fkdict">
      <div className="dbm-fkdict-head">
        References <code>{fk.refTable}</code>.<code>{keyColumn}</code>
      </div>
      <input
        type="text"
        className="dbm-fkdict-search"
        placeholder={`Search ${fk.refTable}`}
        aria-label={`Search ${fk.refTable}`}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {error && <div className="dbm-fkdict-error">{error}</div>}
      <OsScroll className="dbm-fkdict-list">
        {loading && rows === null ? (
          <div className="dbm-fkdict-note">Loading…</div>
        ) : rows && rows.length === 0 ? (
          <div className="dbm-fkdict-note">
            {search ? `Nothing in ${fk.refTable} matches “${search}”` : `${fk.refTable} is empty`}
          </div>
        ) : (
          (rows || []).map((row, i) => {
            const key = cellToText(row[0]);
            const label = row.slice(1).map(cellToText).filter(Boolean).join(' · ');
            return (
              <button
                type="button"
                key={`${key}:${i}`}
                className={`dbm-fkdict-row${key === current ? ' dbm-fkdict-row--current' : ''}`}
                onClick={() => onPick(key)}
                title={label ? `${key} — ${label}` : key}
              >
                <span className="dbm-fkdict-key">{key}</span>
                {label && <span className="dbm-fkdict-label">{label}</span>}
              </button>
            );
          })
        )}
      </OsScroll>
      {rows && rows.length === DICTIONARY_LIMIT && (
        <div className="dbm-fkdict-note">
          Showing the first {DICTIONARY_LIMIT} — search to narrow it.
        </div>
      )}
      {columns && columns.length === 1 && (
        <div className="dbm-fkdict-note">
          {fk.refTable} has no column to label its rows with, so only keys are shown.
        </div>
      )}
    </div>
  );
}
